import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, Pencil, Upload, CheckSquare, Trash2, Save } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RowData {
  id: string;
  account_number?: string;
  property_name?: string;
  unit_number?: string;
  commodity_type?: string;
  vendor_name?: string;
  [key: string]: any;
}

interface ValidationResult {
  isValid: boolean;
  errors: Array<{ field: string; message: string }>;
  warnings: Array<{ field: string; message: string }>;
}

interface RowActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: RowData;
  onEdit: (rowId: string, updates: Partial<RowData>) => Promise<void>;
  onReuploadRow?: (file: File) => Promise<void>;
  onValidate: () => Promise<ValidationResult>;
  onDelete: () => Promise<void>;
  onSave: () => Promise<void>;
  commodities?: string[];
  vendors?: Array<{ id: string; name: string }>;
}

/**
 * RowActionsModal - 5-action modal per row
 * 
 * Requirement 4: Row Actions Modal with Edit, Re-upload, Validate, Delete, Save & Continue
 */
export default function RowActionsModal({
  isOpen,
  onClose,
  row,
  onEdit,
  onReuploadRow,
  onValidate,
  onDelete,
  onSave,
  commodities = [],
  vendors = [],
}: RowActionsModalProps) {
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editedRow, setEditedRow] = useState<Partial<RowData>>({});

  const handleValidate = async () => {
    setIsLoading(true);
    try {
      const result = await onValidate();
      setValidationResult(result);
      setActiveAction('validate');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEdits = async () => {
    if (Object.keys(editedRow).length > 0) {
      setIsLoading(true);
      try {
        await onEdit(row.id, editedRow);
        setEditedRow({});
        setActiveAction(null);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSaveAndClose = async () => {
    setIsLoading(true);
    try {
      if (Object.keys(editedRow).length > 0) {
        await onEdit(row.id, editedRow);
      }
      await onSave();
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const ActionCard = ({
    icon,
    title,
    description,
    onClick,
    isActive,
  }: {
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick: () => void;
    isActive?: boolean;
  }) => (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left p-4 rounded-lg border transition-all',
        'hover:border-primary/50 hover:bg-primary/5',
        isActive && 'border-primary bg-primary/10'
      )}
    >
      <div className="flex items-start gap-3">
        <div className="text-xl">{icon}</div>
        <div>
          <p className="font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
    </button>
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Row Actions - Account #{row.account_number || 'N/A'}</DialogTitle>
            <DialogDescription>
              Manage this account row with the available actions below.
            </DialogDescription>
          </DialogHeader>

          {/* Account Info */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-1">
            <p><strong>Account:</strong> {row.account_number || 'N/A'}</p>
            <p><strong>Property:</strong> {row.property_name || 'N/A'}</p>
            <p><strong>Unit:</strong> {row.unit_number || 'N/A'}</p>
            <p><strong>Commodity:</strong> {row.commodity_type || 'N/A'}</p>
            <p><strong>Vendor:</strong> {row.vendor_name || 'N/A'}</p>
          </div>

          {/* Action Cards */}
          <div className="space-y-2">
            <ActionCard
              icon={<Pencil className="h-5 w-5 text-blue-600" />}
              title="📝 Edit Row Values"
              description="Modify account details inline"
              onClick={() => setActiveAction(activeAction === 'edit' ? null : 'edit')}
              isActive={activeAction === 'edit'}
            />
            
            <ActionCard
              icon={<Upload className="h-5 w-5 text-purple-600" />}
              title="🔄 Re-upload This Row"
              description="Replace this row's data from a file"
              onClick={() => setActiveAction(activeAction === 'reupload' ? null : 'reupload')}
              isActive={activeAction === 'reupload'}
            />
            
            <ActionCard
              icon={<CheckSquare className="h-5 w-5 text-green-600" />}
              title="✅ Validate Row"
              description="Check if all required fields are complete"
              onClick={handleValidate}
              isActive={activeAction === 'validate'}
            />
            
            <ActionCard
              icon={<Trash2 className="h-5 w-5 text-red-600" />}
              title="🗑️ Delete Row"
              description="Remove this account from import"
              onClick={() => setShowDeleteConfirm(true)}
            />
            
            <ActionCard
              icon={<Save className="h-5 w-5 text-primary" />}
              title="💾 Save & Continue"
              description="Save changes and close modal"
              onClick={handleSaveAndClose}
            />
          </div>

          {/* Edit Form Panel */}
          {activeAction === 'edit' && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="pt-6 space-y-4">
                <h4 className="font-medium mb-4">Edit Row Values</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="account_number">Account Number</Label>
                    <Input
                      id="account_number"
                      defaultValue={row.account_number}
                      onChange={(e) => setEditedRow(prev => ({ ...prev, account_number: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="property_name">Property Name</Label>
                    <Input
                      id="property_name"
                      defaultValue={row.property_name}
                      onChange={(e) => setEditedRow(prev => ({ ...prev, property_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit_number">Unit Number</Label>
                    <Input
                      id="unit_number"
                      defaultValue={row.unit_number}
                      onChange={(e) => setEditedRow(prev => ({ ...prev, unit_number: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="commodity_type">Commodity</Label>
                    <Select
                      defaultValue={row.commodity_type}
                      onValueChange={(val) => setEditedRow(prev => ({ ...prev, commodity_type: val }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select commodity" />
                      </SelectTrigger>
                      <SelectContent>
                        {['Electricity', 'Water', 'Gas', 'Sewer', 'Trash', 'Storm Drain', 'Irrigation'].map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                        {commodities.filter(c => !['Electricity', 'Water', 'Gas', 'Sewer', 'Trash', 'Storm Drain', 'Irrigation'].includes(c)).map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="vendor_name">Vendor Name</Label>
                    <Select
                      defaultValue={row.vendor_name}
                      onValueChange={(val) => setEditedRow(prev => ({ ...prev, vendor_name: val }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map(v => (
                          <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleSaveEdits} disabled={isLoading || Object.keys(editedRow).length === 0}>
                  {isLoading ? 'Saving...' : 'Apply Changes'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Re-upload Panel */}
          {activeAction === 'reupload' && (
            <Card className="border-purple-200 bg-purple-50/30">
              <CardContent className="pt-6">
                <h4 className="font-medium mb-4">Re-upload This Row</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Select a CSV/Excel file containing the corrected data for this row.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.csv,.xlsx,.xls';
                    input.onchange = async (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file && onReuploadRow) {
                        await onReuploadRow(file);
                      }
                    };
                    input.click();
                  }}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Select File
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Validation Results Panel */}
          {activeAction === 'validate' && validationResult && (
            <Card className={cn(
              'border-2',
              validationResult.isValid ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'
            )}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  {validationResult.isValid ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <h4 className="font-medium text-green-700">All Fields Valid</h4>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <h4 className="font-medium text-red-700">Validation Issues Found</h4>
                    </>
                  )}
                </div>
                
                {validationResult.errors.length > 0 && (
                  <div className="space-y-2 mb-4">
                    <p className="text-sm font-medium text-red-700">Errors:</p>
                    {validationResult.errors.map((err, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Badge variant="destructive" className="text-xs">{err.field}</Badge>
                        <span>{err.message}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {validationResult.warnings.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-amber-700">Warnings:</p>
                    {validationResult.warnings.map((warn, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">{warn.field}</Badge>
                        <span>{warn.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSaveAndClose} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Apply Actions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account #{row.account_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this account from the import. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
